import React, { useEffect, useState } from 'react';
import { useApp } from '../../context/AppContext';
import { Trash2, Download, Link as LinkIcon } from 'lucide-react';
import './Documents.css';

const card = { background: 'rgba(30,41,59,0.3)', border: '1px solid rgba(255,255,255,0.05)', borderRadius: '1rem' };

export default function Documents() {
  const { documents, fetchDocuments, removeDocument, restoreDocument, patchDocument, clients, fetchClients, projects, fetchProjects, invoices, fetchInvoices } = useApp();
  const [filterClient, setFilterClient] = useState('');
  const [filterProject, setFilterProject] = useState('');
  const [filterInvoice, setFilterInvoice] = useState('');
  const [showArchived, setShowArchived] = useState(false);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(null);

  useEffect(() => { fetchClients(); fetchProjects(); fetchInvoices(); }, [fetchClients, fetchProjects, fetchInvoices]);
  useEffect(() => { load(); }, [filterClient, filterProject, filterInvoice, showArchived]);

  const load = async () => {
    setLoading(true);
    try {
      await fetchDocuments({
        client: filterClient || undefined,
        project: filterProject || undefined,
        invoice: filterInvoice || undefined,
        includeArchived: showArchived || undefined
      });
    } catch (err) {
      setMessage({ type: 'error', text: 'Failed to load documents' });
    } finally {
      setLoading(false);
    }
  };

  const handleArchive = async (doc) => {
    if (!window.confirm(`Archive document "${doc.originalName}"?`)) return;
    try {
      await removeDocument(doc._id);
      setMessage({ type: 'success', text: 'Archived' });
      setTimeout(() => setMessage(null), 3000);
      await load();
    } catch (err) {
      setMessage({ type: 'error', text: 'Archive failed' });
      setTimeout(() => setMessage(null), 3000);
    }
  };

  const handleRestore = async (doc) => {
    try {
      await restoreDocument(doc._id);
      setMessage({ type: 'success', text: 'Restored' });
      setTimeout(() => setMessage(null), 3000);
      await load();
    } catch (err) {
      setMessage({ type: 'error', text: 'Restore failed' });
      setTimeout(() => setMessage(null), 3000);
    }
  };

  const handleAttach = async (docId, field, value) => {
    try {
      await patchDocument(docId, { [field]: value || null });
      setMessage({ type: 'success', text: 'Updated' });
      setTimeout(() => setMessage(null), 3000);
    } catch (err) {
      setMessage({ type: 'error', text: 'Update failed' });
      setTimeout(() => setMessage(null), 3000);
    }
  };

  return (
    <div className="documents-page">
      <div className="documents-head">
        <h1 className="documents-title">Documents ({documents.length})</h1>
        <div className="documents-head-message">
          {message && (
            <div style={{ padding: '0.5rem 0.75rem', borderRadius: '0.5rem', background: message.type === 'success' ? 'rgba(16,185,129,0.12)' : 'rgba(239,68,68,0.08)', color: message.type === 'success' ? '#34d399' : '#f87171' }}>
              {message.text}
            </div>
          )}
        </div>
      </div>

      <div className="documents-filters">
        <select value={filterClient} onChange={(e) => setFilterClient(e.target.value)} className="documents-select">
          <option value="">All clients</option>
          {clients.map((c) => <option key={c._id} value={c._id}>{c.name} - {c.company}</option>)}
        </select>
        <select value={filterProject} onChange={(e) => setFilterProject(e.target.value)} className="documents-select">
          <option value="">All projects</option>
          {projects.map((p) => <option key={p._id} value={p._id}>{p.name}</option>)}
        </select>
        <select value={filterInvoice} onChange={(e) => setFilterInvoice(e.target.value)} className="documents-select">
          <option value="">All invoices</option>
          {invoices.map((inv) => <option key={inv._id} value={inv._id}>{inv.invoiceNumber} - ${inv.total}</option>)}
        </select>
        <button onClick={load} className="documents-filter-btn">{loading ? 'Loading...' : 'Filter'}</button>
        <label className="documents-archived-toggle">
          <input type="checkbox" checked={showArchived} onChange={(e) => setShowArchived(e.target.checked)} />
          <span>Show Archived</span>
        </label>
      </div>

      <div style={{ ...card }} className="documents-table-wrap responsive-table">
        <table>
          <thead>
            <tr style={{ borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
              {['File', 'Client', 'Project', 'Invoice', 'Uploaded', 'Size', 'Actions'].map((h) => (
                <th key={h} style={{ textAlign: 'left', padding: '1rem 1.5rem', fontSize: '0.875rem', fontWeight: 500, color: '#94a3b8' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {documents.map((doc) => (
              <tr key={doc._id} style={{ borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
                <td data-label="File" style={{ padding: '1rem 1.5rem' }}>
                  <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center', flexWrap: 'wrap' }}>
                    <a href={doc.signedUrl || doc.url} target="_blank" rel="noopener noreferrer" style={{ color: '#34d399', display: 'inline-flex', alignItems: 'center', gap: '0.5rem' }}>
                      <Download size={14} />{doc.originalName}
                    </a>
                    {doc.project && <span style={{ fontSize: '0.75rem', padding: '0.125rem 0.5rem', borderRadius: '0.375rem', background: 'rgba(255,255,255,0.02)', color: '#94a3b8' }}>{doc.project.name}</span>}
                  </div>
                </td>
                <td data-label="Client" style={{ padding: '1rem 1.5rem' }}>{doc.client?.name || doc.client?.email}</td>
                <td data-label="Project" style={{ padding: '1rem 1.5rem' }}>
                  <select value={doc.project?._id || ''} onChange={(e) => handleAttach(doc._id, 'project', e.target.value)} className="documents-mini-select" disabled={showArchived}>
                    <option value="">none</option>
                    {projects.map((p) => <option key={p._id} value={p._id}>{p.name}</option>)}
                  </select>
                </td>
                <td data-label="Invoice" style={{ padding: '1rem 1.5rem' }}>
                  <select value={doc.invoice?._id || ''} onChange={(e) => handleAttach(doc._id, 'invoice', e.target.value)} className="documents-mini-select" disabled={showArchived}>
                    <option value="">none</option>
                    {invoices.map((i) => <option key={i._id} value={i._id}>{i.invoiceNumber}</option>)}
                  </select>
                </td>
                <td data-label="Uploaded" style={{ padding: '1rem 1.5rem' }}>{new Date(doc.createdAt).toLocaleString()}</td>
                <td data-label="Size" style={{ padding: '1rem 1.5rem' }}>{(doc.size / 1024).toFixed(1)} KB</td>
                <td data-label="Actions" style={{ padding: '1rem 1.5rem' }}>
                  <div className="documents-actions">
                    <button title="Copy link" onClick={() => { const link = doc.signedUrl || doc.url || ''; navigator.clipboard.writeText(link); setMessage({ type: 'success', text: 'Link copied' }); setTimeout(() => setMessage(null), 2000); }} className="documents-icon-btn"><LinkIcon size={14} /></button>
                    {showArchived ? (
                      <button title="Restore" onClick={() => handleRestore(doc)} className="documents-icon-btn">Restore</button>
                    ) : (
                      <button title="Archive" onClick={() => handleArchive(doc)} className="documents-icon-btn documents-icon-btn--danger"><Trash2 size={14} /></button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {documents.length === 0 && <div style={{ padding: '3rem', textAlign: 'center', color: '#64748b' }}>{loading ? 'Loading...' : 'No documents'}</div>}
      </div>
    </div>
  );
}
